import { BodyText, DefaultButton, TitleMedium } from 'common/presentation/components';
import {
	Alert,
	Box, CircularProgress, Grid, Stack, Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Key } from 'assets/icons';
import TextField from 'common/presentation/components/text-field/TextField';
import Dimens from 'assets/dimens';
import { makeRegisterValidation } from '../builder/authentication-factory';
import useAuth from '../provider/auth.provider';
import { heroLanding } from 'assets/images';
import { useNavigate } from 'react-router-dom';
import AppLinks from 'assets/applinks.routes';

const ResetPasswordPage = () => {
	const { t } = useTranslation('translations');
	const { loading, error, resetPassword } = useAuth();

	const navigate = useNavigate();

	const theme = useTheme();
	const isMd = useMediaQuery(theme.breakpoints.up('md'));

	const queryParameters = new URLSearchParams(window.location.search);
  	const email = queryParameters.get('email') || '';
  	const token = queryParameters.get('token') || '';

	const [password, setPassword] = React.useState({
		password: '',
		repeatPassword: '',
	});

	const [passwordError, setPasswordError] = React.useState({
		password: '',
		repeatPassword: '',
	});

	const [success, setSuccess] = React.useState('');

	const validation = makeRegisterValidation();

	const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const formData = { ...password };
		const passwordErrorV = validation.validate('password', formData);
		const repeatPasswordErrorV = validation.validate('repeatPassword', formData);
		const isFormValid = !(!!passwordErrorV || !!repeatPasswordErrorV);
		setPasswordError({
			password: passwordErrorV,
			repeatPassword: repeatPasswordErrorV,
		});
		if (isFormValid) {
			resetPassword(email, password.password, token).then((result) => {
				if (result) {
					setPassword({
						password: '',
						repeatPassword: '',
					});
					setSuccess(result.toString());
				}
			});
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		setPassword((prev) => ({ ...prev, [event.target.name]: event.target.value }));
		setPasswordError((prev) => ({
			...prev,
			[event.target.name]: '',
		}));
	};

	return (
		<Box sx={{
			backgroundImage: isMd ? `url(${heroLanding})` : 'none',
			backgroundPosition: 'left top',
			backgroundRepeat: 'no-repeat',
			backgroundSize: '50% 100%',
			mt: Dimens.INTRO_HERO_IMAGE_TOP,
		}}
		>
			<Stack
				alignItems="flex-end"
				sx={{
					width: { md: '100%', xs: '100%' },
					pl: { md: Dimens.PAGE_PADDING_SMALL, xs: Dimens.PAGE_PADDING_SMALL_MOBILE },
					pr: Dimens.PAGE_PADDING_SMALL_MOBILE,
					pt: { md: Dimens.INTRO_HERO_IMAGE_TOP_POSITIVE, xs: Dimens.SECTION_SPACING },
				}}
			>
				<Box
					sx={{ width: { md: '50%', xs: '100%' }, height: '85vh' }}
				>
					<Stack justifyContent="center" sx={{ height: '100%' }}>
						<TitleMedium>
							{t('change_password')}
						</TitleMedium>
						<BodyText sx={{ mt: 2 }}>
							{t('change_password_info')}
						</BodyText>
						<Stack mt={5}>
							<Typography color="red">
								{error}
							</Typography>
							{success && (
								<Alert severity="success" sx={{ mb: 2 }}>
									{success}
								</Alert>
							)}
						</Stack>
						<form onSubmit={onSubmit} autoComplete="off">
							<Grid container spacing={2}>
								<Grid item md={8} xs={12}>
									<TextField
										name="password"
										type="password"
										error={!!passwordError.password}
										helperText={passwordError.password}
										placeholder={t('password')}
										fullWidth
										value={password.password}
										onChange={handleChange}
										startIcon={<Key />}
									/>
								</Grid>
								<Grid item md={8} xs={12}>
									<TextField
										name="repeatPassword"
										type="password"
										error={!!passwordError.repeatPassword}
										helperText={passwordError.repeatPassword}
										placeholder={t('repeat_the_password')}
										fullWidth
										value={password.repeatPassword}
										onChange={handleChange}
										startIcon={<Key />}
									/>
								</Grid>
								<Grid item md={8} xs={12} mt={2}>
									<Stack alignItems="center">
										<DefaultButton type="submit" disabled={loading || !!success} sx={{ alignSelf: 'center' }}>
											{loading && <CircularProgress size={30} />}
											{!loading && t('recover_password')}
										</DefaultButton>
									</Stack>
								</Grid>
								<Grid item md={8} xs={12} mt={2}>
									<Stack alignItems="center">
										<DefaultButton onClick={() => navigate(AppLinks.LOGIN)} sx={{ alignSelf: 'center' }}>
											{t('back_to_login')}
										</DefaultButton>
									</Stack>
								</Grid>
							</Grid>
						</form>
					</Stack>
				</Box>
			</Stack>
		</Box>
	);
};

export default ResetPasswordPage;
